import type { ReactNode } from 'react';
import { DateCell } from './DateCell';
import { StackedCell } from './StackedCell';
import { LatestUpdateCell } from './LatestUpdateCell';
import type { OtherInfoData } from './OtherInfoModal';

/**
 * The shape both table shells accept.
 *
 * `ColumnDef` and `DataColumn` are structurally compatible by design — that is what lets
 * `<IndexListView>` take a panel's columns directly — so the audit trio is declared once in
 * the narrower shape and assigned into either.
 */
export interface AuditColumn<T> {
  key: string;
  header: string;
  width: number;
  render: (row: T) => ReactNode;
}

/**
 * `Created · Latest update` — TWO stacked columns, appended by the SHELL from the `otherInfo`
 * every surface already supplies, never declared by a page or a panel.
 *
 * **Each column is the instant over the ACTOR** (product call, 21 Oct 2026). It was three
 * columns — `Created`, `Created by`, `Latest update` — and the update had no actor column at
 * all, so the one question a reader asks of a changed record (*who touched this?*) was only in
 * the Other Info popover. Merging answers it and costs LESS width than the three it replaces:
 * 2 x 220 against 175 + 190 + 175.
 *
 * **220 is MEASURED, not chosen.** `<StackedCell>` wraps its primary in a `truncate` span that
 * `<DateCell>` alone never had, so the old 175 clipped the instant the moment it was nested:
 * the date needs 195px and the cell spends 25px on padding. Re-measure if either the format or
 * the padding moves — a clipped timestamp reads as a shorter one, not as a truncated one.
 *
 * **It is an APPROVED DEVIATION from "one column, one value", not a loophole.** The stack is
 * refused inside an index table by `<StackedCell>`'s own dev error, and `approvedDeviation`
 * silences it only in the files `test:conventions` allows — this one and the Plantilla anchor.
 * What buys the exception is that the pair was ALREADY one fact: the note below has said since
 * the file was written that the date and its actor "are one fact split across two columns and
 * must not read as two weights of information".
 *
 * **The cost is real and accepted**: a record never updated renders ONE line where its
 * neighbours render two, so rows in a mixed list are not all the same height. `useRowHeight`
 * is a floor rather than a clamp, so nothing clips — the rows simply differ.
 *
 * **It lived in `<IndexPage>` alone, and `<DataTablePanel>` had no equivalent**, so all nine
 * personnel section panels hand-rolled a `createdAt` column of their own and not one of them
 * had an update column. Updating a record showed nothing anywhere a reader looks: the panel
 * table had no column for it, and `<RowPreviewModal>` is built from those same columns, so
 * the detail modal could not show it either.
 *
 * That is the rule this file exists to satisfy — when a block is hand-rolled at a second call
 * site it belongs in the shell — and the cost of it living in one shell was nine partial
 * copies, all missing the same half.
 *
 * Details the copies got wrong or left out, kept here so they cannot drift again:
 *
 * - **Not sortable.** The sort allow-list is per-module on the server, and a control that
 *   silently fell back to the default order is worse than no control.
 * - **The ACTOR is the second line, muted** — `<StackedCell>`'s own `text-xs text-slate-400`
 *   against the instant's `text-slate-500`. The date is what the column is scanned down; the
 *   name answers the follow-up. They must not read as two weights of the same information,
 *   which is what the two separate columns did.
 * - **`Latest update` falls back to BLANK, not an em dash**, and **no instant means no
 *   actor** — the second line is withheld on `updatedAt == null` rather than trusted from
 *   `updatedByName`, so a row carrying a stale actor with no timestamp cannot print a name
 *   under a blank date. `Created` takes the dash, because every record has a creation and a
 *   missing one is a fault. Both rules live in `<LatestUpdateCell>` now, with the gate that
 *   reads them — one place deciding whether that cell is a button and what it falls back to.
 * - **`Latest update` is CLICKABLE and `Created` is NOT** (27 Oct 2026). The stamp said WHEN
 *   and WHO and never WHAT, so the cell opens the audit entry behind it, under
 *   `audit-logs.read`. An insert's entry would only say the record appeared, which the row
 *   already says.
 * - **A row with NO provenance renders both blank**, because `otherInfo` may return null for a
 *   row that is DERIVED rather than stored. Such a page passes `auditColumns={false}` and
 *   these are never built at all; tolerated here rather than assumed away, so a page that sets
 *   one and forgets the other loses two columns instead of crashing its whole table.
 */
export function buildAuditColumns<T>(
  otherInfo: (row: T) => OtherInfoData | null,
  /**
   * The row's record id, which is what makes `Latest update` CLICKABLE — see
   * `<LatestUpdateCell>`. Both shells already have it as `rowKey`; left off, the column is
   * exactly the stamp it has always been.
   */
  rowKey?: (row: T) => string,
): AuditColumn<T>[] {
  return [
    {
      key: 'createdAt',
      header: 'Created',
      width: 220,
      render: (row: T) => {
        const audit = otherInfo(row);
        return (
          <StackedCell
            /*
              `<DateCell>` as the PRIMARY rather than a formatted string: it carries the
              `<time dateTime>` wrapper, the mono tabular figures and the cell tone, none of
              which a bare string in this slot would have.
            */
            primary={<DateCell value={audit?.createdAt ?? null} fallback="—" />}
            secondary={audit?.createdByName ?? null}
            mono
            approvedDeviation
          />
        );
      },
    },
    {
      key: 'updatedAt',
      header: 'Latest update',
      width: 220,
      /*
        CLICKABLE, under `audit-logs.read` — it opens the audit entry behind the stamp, which
        is the one follow-up a reader has to *who touched this and when*. `Created` is NOT: an
        insert's entry says only that the record appeared, which the row already says.

        Everything else about this cell — blank rather than an em dash, no actor without an
        instant — moved into `<LatestUpdateCell>` with it, so the gate and the fallbacks are
        decided in one place.
      */
      render: (row: T) => {
        const audit = otherInfo(row);
        return (
          <LatestUpdateCell
            entityId={audit?.auditEntityId ?? (rowKey ? rowKey(row) : null)}
            updatedAt={audit?.updatedAt ?? null}
            updatedByName={audit?.updatedByName ?? null}
          />
        );
      },
    },
  ];
}
